import { getServiceStrings } from '@/services/i18n'
import { SCREEN_SHARE_VIDEO_CONSTRAINTS } from '@/audio/displayCaptureConstraints'

function getMicrophoneConstraints(deviceId?: string): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  }
}

export interface PreparedTabAudioCapture {
  displayStream: MediaStream
  /** Null when the meeting was set up without a microphone — tab audio only, nothing to mix. */
  microphoneStream: MediaStream | null
}

let prepared: PreparedTabAudioCapture | null = null
let activeSession: PreparedTabAudioCapture | null = null

export async function prepareTabAudioCapture(
  microphoneDeviceId: string,
  hasMicrophone: boolean
): Promise<void> {
  clearPreparedTabAudioCapture()

  const displayStream = await navigator.mediaDevices.getDisplayMedia({
    video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  })

  // Keep video tracks alive for screen-capture reuse (one picker). Disable until
  // capture starts — note this blanks the frames but does not stop the capture
  // source, so the low frame rate above is what actually keeps this window cheap.
  displayStream.getVideoTracks().forEach((track) => {
    track.enabled = false
  })

  const audioTracks = displayStream.getAudioTracks()
  if (audioTracks.length === 0) {
    displayStream.getTracks().forEach((track) => track.stop())
    throw new Error(getServiceStrings().audioTrackMissing)
  }

  if (!hasMicrophone) {
    prepared = { displayStream, microphoneStream: null }
    return
  }

  const microphoneStream = await navigator.mediaDevices.getUserMedia({
    audio: getMicrophoneConstraints(microphoneDeviceId),
    video: false,
  })

  if (microphoneStream.getAudioTracks().length === 0) {
    displayStream.getTracks().forEach((track) => track.stop())
    microphoneStream.getTracks().forEach((track) => track.stop())
    throw new Error(getServiceStrings().microphoneTrackMissing)
  }

  prepared = { displayStream, microphoneStream }
}

export function takePreparedTabAudioCapture(): PreparedTabAudioCapture | null {
  const value = prepared
  prepared = null
  return value
}

/** Reuse tab + mic streams across Soniox restarts within the same meeting. */
export function borrowActiveTabAudioCapture(): PreparedTabAudioCapture | null {
  return activeSession
}

export function setActiveTabAudioCapture(capture: PreparedTabAudioCapture): void {
  activeSession = capture
}

export function clearActiveTabAudioCapture(): void {
  activeSession?.displayStream.getTracks().forEach((track) => track.stop())
  activeSession?.microphoneStream?.getTracks().forEach((track) => track.stop())
  activeSession = null
}

export function clearPreparedTabAudioCapture(): void {
  prepared?.displayStream.getTracks().forEach((track) => track.stop())
  prepared?.microphoneStream?.getTracks().forEach((track) => track.stop())
  prepared = null
}

export function hasPreparedTabAudioCapture(): boolean {
  return prepared !== null
}

/** Peek the current tab-share display stream (prepared or active) without taking ownership. */
export function getTabAudioDisplayStream(): MediaStream | null {
  return activeSession?.displayStream ?? prepared?.displayStream ?? null
}
